import React, { useMemo, useState, useEffect } from 'react';
import Navbar from '../../../../../components/navbar.jsx';
import Footer from '../../../../../components/footer.jsx';
import Button from '../../../../../components/Button.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import DatePicker from '../../../../../components/DatePicker.jsx';
import { ReportsSidebar, ReportsTable } from '../../components/index.js';
import { getBranchByCode } from '../../../../../api/branchManagement]/branchApi.js';
import PrintReport from '../../components/PrintReport.jsx';
import styles from '../css/ScriptTotalsPerMedicalAid.module.css';

const ScriptTotalsPerMedicalAid = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [branchInfo, setBranchInfo] = useState(null);

  // Fetch branch information when component mounts
  useEffect(() => {
    const fetchBranchInfo = async () => {
      try {
        // Get user info from localStorage
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
        if (userInfo.branchcode) {
          const branchData = await getBranchByCode(userInfo.branchcode);
          setBranchInfo(branchData);
        }
      } catch (error) {
        console.error('Error fetching branch info:', error);
      }
    };

    fetchBranchInfo();
  }, []);

  const rows = useMemo(
    () => [
      {
        scriptDpn: '554005',
        date: '06/22/2023',
        member: 'John Smith',
        reference: '48740545',
        authorisation: 'VVIVCGL161',
        gross: '2456.80',
        payDue: '1234.50',
        levy: '45.60',
        other: '89.90',
        levy2: '156.70',
        medAid: 'MEDICALAID',
      },
      {
        scriptDpn: '0112496',
        date: '06/13/2022',
        member: 'Sarah Johnson',
        reference: '7810278612',
        authorisation: 'DIR46n',
        gross: '1890.45',
        payDue: '945.22',
        levy: '28.50',
        other: '67.80',
        levy2: '234.10',
        medAid: 'HEALTHCARE',
      },
      {
        scriptDpn: '205842',
        date: '05/01/2022',
        member: 'Michael Brown',
        reference: '54978105',
        authorisation: 'MED12345',
        gross: '3245.60',
        payDue: '1622.80',
        levy: '52.30',
        other: '98.40',
        levy2: '345.20',
        medAid: 'CAREPLUS',
      },
      {
        scriptDpn: '987654',
        date: '04/15/2023',
        member: 'Emily Davis',
        reference: '123456789',
        authorisation: 'AUTH67890',
        gross: '1567.90',
        payDue: '783.95',
        levy: '22.10',
        other: '45.60',
        levy2: '189.30',
        medAid: 'MEDICOVER',
      },
      {
        scriptDpn: '456789',
        date: '03/28/2023',
        member: 'Robert Wilson',
        reference: '987654321',
        authorisation: 'PRE11223',
        gross: '2890.75',
        payDue: '1445.38',
        levy: '41.20',
        other: '78.90',
        levy2: '267.40',
        medAid: 'HEALTHSA',
      },
    ],
    []
  );

  const rowsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = rows.slice(startIndex, endIndex);

  const columns = useMemo(
    () => [
      { header: 'Script#DPN', accessor: 'scriptDpn' },
      { header: 'Date', accessor: 'date' },
      { header: 'Member', accessor: 'member' },
      { header: 'Reference', accessor: 'reference' },
      { header: 'Authorisation', accessor: 'authorisation' },
      { header: 'Gross', accessor: 'gross' },
      { header: 'Pay Due', accessor: 'payDue' },
      { header: 'Levy', accessor: 'levy' },
      { header: 'Other', accessor: 'other' },
      { header: 'Levy', accessor: 'levy2' },
      { header: 'Med Aid', accessor: 'medAid' },
    ],
    []
  );

  const handlePrint = () => {
    PrintReport({
      reportType: 'scriptTotals',
      reportTitle: 'SCRIPT TOTALS PER MEDICAL AID REPORT',
      branchInfo,
      headers: ['Script#DPN', 'Date', 'Member', 'Reference', 'Authorisation', 'Gross', 'Pay Due', 'Levy', 'Other', 'Levy 2', 'Med Aid'],
      data: rows, // Use all rows for print (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: true
    });
  };

  const handleExport = () => {
    PrintReport({
      reportType: 'scriptTotals',
      reportTitle: 'SCRIPT TOTALS PER MEDICAL AID REPORT',
      branchInfo,
      headers: ['Script#DPN', 'Date', 'Member', 'Reference', 'Authorisation', 'Gross', 'Pay Due', 'Levy', 'Other', 'Levy 2', 'Med Aid'],
      data: rows, // Use all rows for export (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: false
    });
  };

  return (
    <div className={styles.container}>
      <Navbar backTo="/customer/dispensary" backReplace />

      <div className={styles.mainContent}>
        <div className={styles.layout}>
          <div className={styles.sidebarContainer}>
            <ReportsSidebar />
          </div>

          <div className={styles.content}>
            <div className={styles.reportContainer}>
              <div className={styles.dateFiltersSection}>
                <div className={styles.dateFilters}>
                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>From</label>
                    <DatePicker
                      selectedDate={fromDate}
                      onDateChange={setFromDate}
                      placeholder=""
                      className={styles.dateInput}
                      disableFutureDates={true}
                    />
                  </div>

                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>To</label>
                    <DatePicker
                      selectedDate={toDate}
                      onDateChange={setToDate}
                      placeholder=""
                      className={styles.dateInput}
                      disableFutureDates={true}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.actionButtonsSection}>
                <div className={styles.actionButtons}>
                  <Button onClick={handlePrint} variant="approve" className={styles.printButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9V2h12v7" />
                      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 01-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Print
                  </Button>

                  <Button onClick={handleExport} variant="primary" className={styles.exportButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </Button>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <ReportsTable columns={columns} data={pageRows} striped={true} />
              </div>

              <div className={styles.paginationContainer}>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ScriptTotalsPerMedicalAid;
